import {StateId} from '@wallaby-ui/core';
import {createSelector} from 'reselect';
import {RootState} from 'src/js/store';

interface SelectorProps {
  detailId?: string | number;
  reportsResult?: Record<string, any>;
  reportId?: string;
  reportsStateId: StateId;
  reportConfig: any;
}

const getReportsResult = (
  state: RootState,
  props: Pick<SelectorProps, 'reportsResult' | 'reportsStateId'>
) => props.reportsResult || state[props.reportsStateId].reportsResult;
const getPropsReportConfig = (
  state: RootState,
  props: Pick<SelectorProps, 'reportConfig'>
) => props.reportConfig;
const getPropsReportId = (
  state: RootState,
  props: Pick<SelectorProps, 'reportId'>
) => props.reportId;
const getPropsDetailId = (
  state: RootState,
  props: Pick<SelectorProps, 'detailId'>
) => props.detailId;

const STATIC_EMPTY_ARRAY = [];

export const getCurrentReport = createSelector(
  [getReportsResult, getPropsReportId, getPropsDetailId],
  (reportResult, reportId, detailId) => {
    const cachedReportId = `${detailId}|${reportId}`;
    return reportResult.cached
      ? reportResult.cached[cachedReportId]
      : reportResult;
  }
);

export const getCurrentDetailId = createSelector(
  [getReportsResult],
  (reportResult) => reportResult.currentDetailId
);

export const getCurrentReportFilteredRowsCount = createSelector(
  [getCurrentReport, getPropsReportConfig],
  (currentReport, reportConfig) =>
    currentReport &&
    currentReport.gridParams &&
    currentReport.gridParams[reportConfig.getId()]
      ? currentReport.gridParams[reportConfig.getId()].filteredRowsCount
      : undefined
);

export const getCurrentReportItems = createSelector(
  [getCurrentReport],
  (currentReport) =>
    currentReport && currentReport.items ? currentReport.items : currentReport
);

export const getDataSourceKey = createSelector(
  [getPropsReportConfig],
  (reportConfig) => {
    return reportConfig && reportConfig.getDataSource()
      ? reportConfig.getDataSource()
      : undefined;
  }
);

export const getReportData = createSelector(
  [getCurrentReportItems, getDataSourceKey],
  (reportData, dataSourceKey) => {
    return reportData && dataSourceKey && reportData[dataSourceKey]
      ? reportData[dataSourceKey]
      : STATIC_EMPTY_ARRAY;
  }
);

export const getCurrentReportSort = createSelector(
  [getCurrentReport, getPropsReportConfig],
  (currentReport, reportConfig) =>
    currentReport &&
    currentReport.gridParams &&
    currentReport.gridParams[reportConfig.getId()]
      ? currentReport.gridParams[reportConfig.getId()].sort
      : undefined
);

export const getCurrentReportPagination = createSelector(
  [getCurrentReport, getPropsReportConfig],
  (currentReport, reportConfig) =>
    currentReport &&
    currentReport.gridParams &&
    currentReport.gridParams[reportConfig.getId()]
      ? currentReport.gridParams[reportConfig.getId()].pagination
      : undefined
);

export const getCurrentReportTotalElements = createSelector(
  [getCurrentReport, getPropsReportConfig],
  (currentReport, reportConfig) =>
    currentReport &&
    currentReport.gridParams &&
    currentReport.gridParams[reportConfig.getId()]
      ? currentReport.gridParams[reportConfig.getId()].totalElements
      : undefined
);

export const getCurrentReportFilters = createSelector(
  [getCurrentReport, getPropsReportConfig],
  (currentReport, reportConfig) =>
    currentReport &&
    currentReport.gridParams &&
    currentReport.gridParams[reportConfig.getId()]
      ? currentReport.gridParams[reportConfig.getId()].filters
      : undefined
);

export const getCurrentReportScroll = createSelector(
  [getCurrentReport, getPropsReportConfig],
  (currentReport, reportConfig) =>
    currentReport &&
    currentReport.gridParams &&
    currentReport.gridParams[reportConfig.getId()]
      ? currentReport.gridParams[reportConfig.getId()].scroll
      : undefined
);

export const getCurrentReportPerformedActions = createSelector(
  [getCurrentReport, getPropsReportConfig],
  (currentReport, reportConfig) =>
    currentReport &&
    currentReport.performedActions &&
    currentReport.performedActions[reportConfig.getId()]
      ? currentReport.performedActions[reportConfig.getId()]
      : STATIC_EMPTY_ARRAY
);

export const isCurrentReportPerformingAction = createSelector(
  [getCurrentReportPerformedActions],
  (currentReportPerformedActions) => currentReportPerformedActions.length > 0
);
